import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import { notificationAPI } from "../../services/api";
import adminApi from "../../services/adminApi";
import {
  FiBell, FiSend, FiClock, FiCheckCircle, FiCheck, FiX,
  FiEdit3, FiTrash2, FiEye, FiDownload, FiUsers,
  FiTrendingUp, FiMail, FiSmartphone, FiZap,
  FiCalendar,  FiRefreshCw, FiVideo, FiActivity, FiSmile,
  FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight
} from "react-icons/fi";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive:"#6B8A5A", olive2:"#8BA47A",
  orange:"#F29A0C", orange2:"#E97818",
  dark:"#1F1F1F", mid:"#555", muted:"#AAA", light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E", greenDk:"#16A34A",
  red:"#E24B4A", amber:"#F59E0B", blue:"#185FA5", purple:"#534AB7",
};

const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06),inset 0 1px 0 #fff",
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;
const TIERS     = ["All Users","Free","Basic","Standard","Premium","YTT Pro"];
const SEGMENTS  = ["All Users","Expiring Soon","Inactive 14d+","New Registrations","Near Completion","Failed Payments","Health Flagged"];
const CHANNELS  = [
  { id:"push",  label:"Push Notification", icon:FiSmartphone, desc:"Instant mobile & web push" },
  { id:"email", label:"Email",             icon:FiMail,        desc:"Delivered to inbox" },
  { id:"both",  label:"Both",              icon:FiZap,         desc:"Push + Email combined" },
];
const STATUS_S = {
  Sent:      { bg:"rgba(34,197,94,0.1)",  c:"#16A34A", dot:"#22C55E" },
  Scheduled: { bg:"rgba(24,95,165,0.1)",  c:"#185FA5", dot:"#378ADD" },
  Failed:    { bg:"rgba(226,75,74,0.1)",  c:"#E24B4A", dot:"#F87171" },
  Draft:     { bg:"rgba(245,158,11,0.1)", c:"#B45309", dot:"#F59E0B" },
};

// ─── TEMPLATES ────────────────────────────────────────────────────────────────
const TEMPLATES = [
  { id:1, name:"Session Reminder",    icon:FiClock,        title:"Your session starts in 30 min", body:"Don't forget, your {session_name} with {instructor} begins at {time}. Join now and make the most of your wellness journey.", audience:"Registered users", channel:"push" },
  { id:2, name:"Subscription Expiry", icon:FiCalendar,     title:"Your plan expires in {days} days", body:"Hi {name}, your {tier} subscription expires on {date}. Renew now to continue your uninterrupted wellness journey.", audience:"Expiring Soon", channel:"both" },
  { id:3, name:"New Content Alert",   icon:FiVideo,        title:"New video just dropped!",       body:"A brand new {category} session by {instructor} is now available for you. Jump in and elevate your practice today.", audience:"All Users", channel:"push" },
  { id:4, name:"Welcome Message",     icon:FiSmile,        title:"Welcome to Divyyog Foundation!",   body:"Hi {name}, we're so glad you're here! Start your wellness journey today with our beginner-friendly yoga sessions and personalised diet plans.", audience:"New Registrations", channel:"both" },
  { id:5, name:"Re-engagement",       icon:FiActivity,     title:"We miss you, {name}!",              body:"It's been a while! Your wellness journey is waiting. Come back and join a live session or explore new content curated just for you.", audience:"Inactive 14d+", channel:"both" },
  { id:6, name:"Diet Plan Published", icon:FiZap,          title:"Your weekly diet plan is ready",   body:"Your personalised {tier} wellness meal plan for {week} is now live. Start your nourishing week with balanced, yoga-aligned nutrition.", audience:"All Users", channel:"email" },
];

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const s = STATUS_S[status] || STATUS_S.Draft;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c }}>
      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background:s.dot }}/>
      {status}
    </span>
  );
};

const ChannelBadge = ({ channel }) => {
  const cfg = {
    push:  { icon:FiSmartphone, label:"Push",  color:T.olive,  bg:"rgba(107,138,90,0.1)" },
    email: { icon:FiMail,       label:"Email", color:T.blue,   bg:"rgba(24,95,165,0.1)" },
    both:  { icon:FiZap,        label:"Both",  color:T.orange, bg:"rgba(242,154,12,0.1)" },
  }[channel] || { icon:FiBell, label:channel, color:T.muted, bg:"rgba(200,200,200,0.1)" };
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
      style={{ background:cfg.bg, color:cfg.color }}>
      <cfg.icon size={11}/>{cfg.label}
    </span>
  );
};

const StatCard = ({ icon:Icon, value, label, trend, trendUp=true, iconBg, accentColor, cardBg, delay=0 }) => (
  <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay }}
    whileHover={{ y:-4, boxShadow:"0 22px 50px rgba(0,0,0,0.1)" }}
    className="ad-kpi-card ad-kpi-flat relative overflow-hidden rounded-[20px] p-4 border"
    style={{ background:cardBg||"rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.9)", transition:"all 0.28s" }}>
    <div className="ad-kpi-icon w-9 h-9 rounded-[11px] flex items-center justify-center mb-3" style={{ background:iconBg }}>
      <Icon size={16} style={{ color:"#fff" }}/>
    </div>
    <p className="ad-kpi-val font-black text-[#1F1F1F] mb-1"
      style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px", lineHeight:1 }}>{value}</p>
    <p className="ad-kpi-lbl text-[10px] font-bold uppercase tracking-[0.8px] mb-1.5" style={{ color:T.muted }}>{label}</p>
    <p className="ad-kpi-note text-[11px] font-bold flex items-center gap-1" style={{ color:trendUp?T.greenDk:T.red }}>
      <FiTrendingUp size={11}/>{trend}
    </p>
    <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-[20px]" style={{ background:accentColor }}/>
  </motion.div>
);

// ─── NOTIFICATION DETAIL DRAWER ───────────────────────────────────────────────
const NotifDrawer = ({ notif, onClose, showToast, onResend, onDuplicate, onExport }) => {
  if (!notif) return null;
  const deliveryPct = Math.round(notif.delivered / notif.recipients * 100);
  const openPct     = Math.round(notif.opened    / notif.recipients * 100);

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100]"
        style={{ background:"rgba(15,15,15,0.2)", backdropFilter:"blur(4px)" }}
        onClick={onClose}/>
      <motion.div initial={{ x:28, opacity:0 }} animate={{ x:0, opacity:1 }}
        exit={{ x:28, opacity:0 }} transition={{ duration:0.35, ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101]"
        style={{ width:420, background:"rgba(255,253,248,0.97)",
          borderLeft:`1px solid rgba(239,229,216,0.7)`,
          boxShadow:"-24px 0 80px rgba(0,0,0,0.1)", scrollbarWidth:"none" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ background:"rgba(255,253,248,0.95)", backdropFilter:"blur(20px)",
            borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[13px] font-bold text-[#1F1F1F]">Notification Detail</p>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>

        {/* Content card preview */}
        <div className="px-6 py-5 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
            style={{ background:"radial-gradient(circle,rgba(107,138,90,0.1),transparent)", filter:"blur(40px)" }}/>
          {/* Phone mockup */}
          <div className="rounded-[20px] p-4 mb-4"
            style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
              boxShadow:"0 16px 40px rgba(0,0,0,0.18)" }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0"
                style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                <span className="text-white font-black text-[14px]" style={{ fontFamily:"'Poppins',sans-serif" }}>DY</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-bold text-white">Divyyog Foundation</span>
                  <span className="text-[10px]" style={{ color:"rgba(255,255,255,0.4)" }}>now</span>
                </div>
                <p className="text-[13px] font-bold text-white leading-tight">{notif.title}</p>
                <p className="text-[11px] mt-1 leading-relaxed" style={{ color:"rgba(255,255,255,0.6)" }}>
                  Tap to open the app and continue your wellness journey.
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <StatusPill status={notif.status}/>
            <ChannelBadge channel={notif.channel}/>
          </div>
        </div>

        {/* Delivery stats */}
        <div className="grid grid-cols-3 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          {[
            [notif.recipients.toLocaleString(), "Recipients", T.orange],
            [notif.delivered.toLocaleString(),  "Delivered",  T.olive],
            [notif.opened.toLocaleString(),      "Opened",     T.blue],
          ].map(([v,l,c]) => (
            <div key={l} className="py-4 text-center border-r last:border-r-0"
              style={{ borderColor:"rgba(239,229,216,0.5)" }}>
              <p className="font-black" style={{ fontFamily:"'Poppins',sans-serif",
                fontSize:18, color:c, letterSpacing:"-1px" }}>{v}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.6px] mt-0.5"
                style={{ color:T.muted }}>{l}</p>
            </div>
          ))}
        </div>

        {/* Performance bars */}
        <div className="px-6 py-5 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-4" style={{ color:T.light }}>
            Delivery Performance
          </p>
          {[
            { label:"Delivery Rate", pct:deliveryPct, color:T.olive,  grad:`linear-gradient(90deg,${T.olive},${T.olive2})` },
            { label:"Open Rate",     pct:openPct,     color:T.orange, grad:`linear-gradient(90deg,${T.orange},${T.orange2})` },
          ].map(m => (
            <div key={m.label} className="mb-4">
              <div className="flex justify-between text-[11px] mb-2">
                <span style={{ color:T.mid, fontWeight:600 }}>{m.label}</span>
                <span className="font-black" style={{ color:m.color, fontFamily:"'Poppins',sans-serif" }}>
                  {m.pct}%
                </span>
              </div>
              <div className="h-[6px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
                <motion.div className="h-full rounded-full"
                  initial={{ width:0 }} animate={{ width:`${m.pct}%` }}
                  transition={{ duration:0.9, ease:"easeOut" }}
                  style={{ background:m.grad }}/>
              </div>
            </div>
          ))}
        </div>

        {/* Details */}
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Details</p>
          {[
            ["Audience",      notif.audience],
            ["Channel",       notif.channel.charAt(0).toUpperCase()+notif.channel.slice(1)],
            ["Sent At",       notif.sent !== " " ? notif.sent : notif.scheduledFor],
            ["Status",        notif.status],
          ].map(([k,v]) => (
            <div key={k} className="flex justify-between items-center py-2 border-b"
              style={{ borderColor:"rgba(239,229,216,0.35)" }}>
              <span className="text-[12px]" style={{ color:T.muted }}>{k}</span>
              <span className="text-[13px] font-semibold text-[#1F1F1F]">{v}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 py-5">
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Actions</p>
          <div className="flex flex-col gap-2.5">
            <motion.button whileHover={{ y:-1 }}
              onClick={() => { onResend(notif); onClose(); }}
              className="w-full h-10 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiRefreshCw size={14}/> Resend Notification
            </motion.button>
            <div className="grid grid-cols-2 gap-2.5">
              <motion.button whileHover={{ y:-1 }}
                onClick={() => { onDuplicate(notif); onClose(); }}
                className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                  color:T.mid, fontFamily:"'Inter',sans-serif" }}>
                <FiEdit3 size={13}/> Duplicate
              </motion.button>
              <motion.button whileHover={{ y:-1 }}
                onClick={() => onExport(notif)}
                className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                  color:T.mid, fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Export
              </motion.button>
            </div>
            {notif.status === "Scheduled" && (
              <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }}
                onClick={() => { showToast("Scheduled notification cancelled"); onClose(); }}
                className="w-full h-10 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 cursor-pointer border"
                style={{ background:"rgba(226,75,74,0.08)", color:T.red,
                  borderColor:"rgba(226,75,74,0.2)", fontFamily:"'Inter',sans-serif", transition:"background 0.2s" }}>
                <FiTrash2 size={13}/> Cancel Notification
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};

// ─── CUSTOM SCHEDULE PICKER COMPONENT ──────────────────────────────────────────
const CustomSchedulePicker = ({ schedDate, schedTime, onDateChange, onTimeChange, errors, touched }) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [calYear, setCalYear] = useState(() => {
    if (schedDate) return new Date(schedDate).getFullYear();
    return new Date().getFullYear();
  });
  const [calMonth, setCalMonth] = useState(() => {
    if (schedDate) return new Date(schedDate).getMonth();
    return new Date().getMonth();
  });

  const { hour: initHour, minute: initMin, ampm: initAmPm } = useMemo(() => {
    if (!schedTime) return { hour: "09", minute: "00", ampm: "AM" };
    const [hStr, mStr] = schedTime.split(":");
    let h = parseInt(hStr || "9", 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return {
      hour: String(h).padStart(2, "0"),
      minute: String(mStr || "00").padStart(2, "0"),
      ampm,
    };
  }, [schedTime]);

  const [selectedHour, setSelectedHour] = useState(initHour);
  const [selectedMin, setSelectedMin] = useState(initMin);
  const [selectedAmPm, setSelectedAmPm] = useState(initAmPm);
  const [hourOpen, setHourOpen] = useState(false);
  const [minOpen, setMinOpen] = useState(false);

  useEffect(() => {
    if (schedTime) {
      const [hStr, mStr] = schedTime.split(":");
      let h = parseInt(hStr || "9", 10);
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12;
      if (h === 0) h = 12;
      setSelectedHour(String(h).padStart(2, "0"));
      setSelectedMin(String(mStr || "00").padStart(2, "0"));
      setSelectedAmPm(ampm);
    }
  }, [schedTime]);

  const updateTime = (h, m, ap) => {
    setSelectedHour(h);
    setSelectedMin(m);
    setSelectedAmPm(ap);
    let hourNum = parseInt(h, 10);
    if (ap === "PM" && hourNum < 12) hourNum += 12;
    if (ap === "AM" && hourNum === 12) hourNum = 0;
    const time24 = `${String(hourNum).padStart(2, "0")}:${m}`;
    onTimeChange(time24);
  };

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const startDayOfWeek = new Date(calYear, calMonth, 1).getDay();

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const isPrevMonthDisabled = calYear < today.getFullYear() || (calYear === today.getFullYear() && calMonth <= today.getMonth());

  const handlePrevMonth = () => {
    if (isPrevMonthDisabled) return;
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear(prev => prev - 1);
    } else {
      setCalMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear(prev => prev + 1);
    } else {
      setCalMonth(prev => prev + 1);
    }
  };

  const dayCells = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    dayCells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const cellDate = new Date(calYear, calMonth, d);
    cellDate.setHours(0, 0, 0, 0);
    const isPast = cellDate < today;
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const isSelected = schedDate === dateStr;
    const isToday = cellDate.getTime() === today.getTime();

    dayCells.push({ d, cellDate, isPast, dateStr, isSelected, isToday });
  }

  return (
    <div className="flex flex-col gap-3 p-4 rounded-[20px] bg-amber-50/50 border border-amber-200/80 shadow-sm">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-1">
        <span className="font-extrabold text-[13px] text-zinc-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
          {monthNames[calMonth]} {calYear}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={isPrevMonthDisabled}
            onClick={handlePrevMonth}
            className="w-7 h-7 rounded-lg flex items-center justify-center border border-zinc-200 bg-white text-zinc-600 hover:bg-amber-100 disabled:opacity-30 disabled:pointer-events-none cursor-pointer transition-colors"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            className="w-7 h-7 rounded-lg flex items-center justify-center border border-zinc-200 bg-white text-zinc-600 hover:bg-amber-100 cursor-pointer transition-colors"
          >
            ›
          </button>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 text-center text-[10.5px] font-black text-amber-800 uppercase tracking-wider">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(dayName => (
          <div key={dayName} className="py-1">{dayName}</div>
        ))}
      </div>

      {/* Custom Calendar Days Grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {dayCells.map((cell, idx) => {
          if (!cell) return <div key={`empty-${idx}`} className="h-8" />;
          return (
            <button
              key={cell.dateStr}
              type="button"
              disabled={cell.isPast}
              onClick={() => onDateChange(cell.dateStr)}
              className={`h-8 rounded-xl text-[12px] font-bold flex items-center justify-center transition-all border ${
                cell.isPast
                  ? "bg-zinc-100/70 text-zinc-300 border-transparent cursor-not-allowed opacity-40 line-through"
                  : cell.isSelected
                  ? "bg-amber-500 text-white border-amber-600 shadow-md font-black"
                  : cell.isToday
                  ? "bg-white text-amber-700 border-amber-400 font-extrabold hover:bg-amber-100"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-amber-100/80 hover:border-amber-300 cursor-pointer"
              }`}
            >
              {cell.d}
            </button>
          );
        })}
      </div>

      {/* Custom 12-Hour Time Format Selector (Custom Dropdowns) */}
      <div className="pt-3 border-t border-amber-200/60 flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-black uppercase tracking-[0.9px] text-amber-900 flex items-center gap-1.5">
            <FiClock size={13} className="text-amber-600" /> Broadcast Time
          </span>

          {/* AM / PM Segment Switch Pill */}
          <div className="flex rounded-xl overflow-hidden border border-amber-300 p-0.5 bg-amber-100/70 shadow-inner h-9 items-center flex-shrink-0">
            {["AM", "PM"].map(ap => (
              <button
                key={ap}
                type="button"
                onClick={() => updateTime(selectedHour, selectedMin, ap)}
                className={`px-3 py-1 text-[11px] font-black rounded-lg transition-all cursor-pointer border-none h-full flex items-center justify-center ${
                  selectedAmPm === ap
                    ? "bg-amber-500 text-white shadow-sm scale-105"
                    : "bg-transparent text-zinc-600 hover:text-zinc-900"
                }`}
              >
                {ap}
              </button>
            ))}
          </div>
        </div>

        {/* Dropdowns Row: Grid 2 Columns */}
        <div className="grid grid-cols-2 gap-2">
          {/* Custom Hour Dropdown */}
          <div className="relative">
            <label className="text-[9.5px] font-extrabold text-zinc-400 uppercase tracking-wider block mb-1">Hour</label>
            <button
              type="button"
              onClick={() => { setHourOpen(!hourOpen); setMinOpen(false); }}
              className="w-full h-10 px-3 rounded-xl border border-amber-300 bg-white hover:border-amber-400 font-bold text-[13px] text-zinc-800 flex items-center justify-between shadow-sm cursor-pointer transition-all outline-none"
            >
              <span className="flex items-center gap-1">
                <span className="text-amber-700 font-extrabold text-[13.5px]">{selectedHour}</span>
                <span className="text-[11px] text-zinc-400 font-medium">Hour</span>
              </span>
              <FiChevronDown size={14} className={`text-amber-600 transition-transform duration-200 ${hourOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {hourOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHourOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 4, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 right-0 top-full z-50 mt-1 p-1 bg-white border border-amber-200 rounded-2xl shadow-2xl max-h-44 overflow-y-auto"
                  >
                    {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map(h => {
                      const todayStr = new Date().toISOString().split("T")[0];
                      let isPastTime = false;
                      if (schedDate === todayStr) {
                        let hourNum = parseInt(h, 10);
                        if (selectedAmPm === "PM" && hourNum < 12) hourNum += 12;
                        if (selectedAmPm === "AM" && hourNum === 12) hourNum = 0;
                        const checkDate = new Date();
                        checkDate.setHours(hourNum, parseInt(selectedMin, 10), 0, 0);
                        isPastTime = checkDate <= new Date();
                      }
                      const isSel = selectedHour === h;

                      return (
                        <button
                          key={h}
                          type="button"
                          disabled={isPastTime}
                          onClick={() => {
                            updateTime(h, selectedMin, selectedAmPm);
                            setHourOpen(false);
                          }}
                          className={`w-full px-2.5 py-1.5 text-left text-[12px] font-bold rounded-xl flex items-center justify-between transition-colors border-none cursor-pointer mb-0.5 ${
                            isPastTime
                              ? "text-zinc-300 bg-zinc-50 cursor-not-allowed opacity-40 line-through"
                              : isSel
                              ? "bg-amber-500 text-white font-extrabold shadow-sm"
                              : "text-zinc-700 hover:bg-amber-100/80 hover:text-amber-900"
                          }`}
                        >
                          <span>{h} Hour</span>
                          {isSel && <FiCheck size={12} className="text-white" />}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Custom Editable Minute Input & 60-Minute Dropdown */}
          <div className="relative">
            <label className="text-[9.5px] font-extrabold text-zinc-400 uppercase tracking-wider block mb-1">Minute</label>
            <div className="w-full h-10 px-2.5 rounded-xl border border-amber-300 bg-white hover:border-amber-400 font-bold text-[13px] text-zinc-800 flex items-center justify-between shadow-sm transition-all focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-200">
              <div className="flex items-center gap-1 flex-1">
                <span className="text-amber-700 font-extrabold text-[13.5px]">:</span>
                <input
                  type="text"
                  maxLength={2}
                  value={selectedMin}
                  onFocus={() => { setMinOpen(true); setHourOpen(false); }}
                  onChange={(e) => {
                    const rawVal = e.target.value.replace(/\D/g, "");
                    if (rawVal === "") {
                      setSelectedMin("");
                      onTimeChange(`${selectedHour}:00`);
                    } else {
                      const num = Math.min(59, Math.max(0, parseInt(rawVal, 10)));
                      const padStr = rawVal.length === 2 ? String(num).padStart(2, "0") : String(rawVal);
                      setSelectedMin(padStr);
                      updateTime(selectedHour, String(num).padStart(2, "0"), selectedAmPm);
                    }
                  }}
                  onBlur={() => {
                    const num = parseInt(selectedMin || "0", 10);
                    const safeNum = isNaN(num) ? 0 : Math.min(59, Math.max(0, num));
                    const finalMin = String(safeNum).padStart(2, "0");
                    setSelectedMin(finalMin);
                    updateTime(selectedHour, finalMin, selectedAmPm);
                  }}
                  placeholder="00"
                  className="w-10 bg-transparent border-none outline-none font-extrabold text-[13.5px] text-amber-700 text-center"
                />
                <span className="text-[11px] text-zinc-400 font-medium">Min</span>
              </div>
              <button
                type="button"
                onClick={() => { setMinOpen(!minOpen); setHourOpen(false); }}
                className="p-1 hover:bg-amber-50 rounded-lg border-none cursor-pointer text-amber-600 transition-colors"
              >
                <FiChevronDown size={14} className={`transition-transform duration-200 ${minOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            <AnimatePresence>
              {minOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMinOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 4, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 1 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 right-0 top-full z-50 mt-1 p-1 bg-white border border-amber-200 rounded-2xl shadow-2xl max-h-48 overflow-y-auto"
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(m => {
                      const todayStr = new Date().toISOString().split("T")[0];
                      let isPastTime = false;
                      if (schedDate === todayStr) {
                        let hourNum = parseInt(selectedHour, 10);
                        if (selectedAmPm === "PM" && hourNum < 12) hourNum += 12;
                        if (selectedAmPm === "AM" && hourNum === 12) hourNum = 0;
                        const checkDate = new Date();
                        checkDate.setHours(hourNum, parseInt(m, 10), 0, 0);
                        isPastTime = checkDate <= new Date();
                      }
                      const isSel = selectedMin === m;

                      return (
                        <button
                          key={m}
                          type="button"
                          disabled={isPastTime}
                          onClick={() => {
                            updateTime(selectedHour, m, selectedAmPm);
                            setMinOpen(false);
                          }}
                          className={`w-full px-2.5 py-1.5 text-left text-[12px] font-bold rounded-xl flex items-center justify-between transition-colors border-none cursor-pointer mb-0.5 ${
                            isPastTime
                              ? "text-zinc-300 bg-zinc-50 cursor-not-allowed opacity-40 line-through"
                              : isSel
                              ? "bg-amber-500 text-white font-extrabold shadow-sm"
                              : "text-zinc-700 hover:bg-amber-100/80 hover:text-amber-900"
                          }`}
                        >
                          <span>:{m} Min</span>
                          {isSel && <FiCheck size={12} className="text-white" />}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Selected Schedule Live Summary Badge */}
      {schedDate && (
        <div className="mt-1 p-2.5 rounded-xl bg-amber-100/70 border border-amber-300/80 flex items-center justify-between text-[11.5px] font-bold text-amber-900">
          <span className="flex items-center gap-1.5">
            <FiCalendar size={13} className="text-amber-700" />
            <span>Date: {schedDate} · {selectedHour}:{selectedMin} {selectedAmPm}</span>
          </span>
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
            Future Date
          </span>
        </div>
      )}

      {/* Validation Errors */}
      {(errors.schedDate && touched.schedDate) || (errors.schedTime && touched.schedTime) ? (
        <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1">
          <FiX size={12}/> {errors.schedDate || errors.schedTime}
        </p>
      ) : null}
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AdminNotificationsPage() {
  // Compose state
  const [title,    setTitle]    = useState("");
  const [body,     setBody]     = useState("");
  const [audience, setAudience] = useState("All Users");
  const [audienceType, setAudienceType] = useState("segment"); // segment | tier
  const [channel,  setChannel]  = useState("push");
  const [scheduled, setScheduled] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [sending,  setSending]  = useState(false);

  // Validation & Touched state
  const [errors, setErrors] = useState({ title: "", body: "", schedDate: "", schedTime: "" });
  const [touched, setTouched] = useState({ title: false, body: false, schedDate: false, schedTime: false });

  // Table state
  const [search,   setSearch]   = useState("");
  const [statusF,  setStatusF]  = useState("all");
  const [tab,      setTab]      = useState("history"); // history | scheduled | templates
  const [drawerId, setDrawerID] = useState(null);
  const [toast,    setToast]    = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [statusFOpen, setStatusFOpen] = useState(false);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteIsScheduled, setDeleteIsScheduled] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Pagination and users state
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 10 rows by default, with 15, 25, 50, all options

  // Real Database History State
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // ── Validation Rules & Regex ─────────────────────────────────────────────
  const TITLE_REGEX = useMemo(() => /^[a-zA-Z0-9\s.,!?'"()\-_&/%:;+*#@\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+$/u, []);
  const BODY_REGEX  = useMemo(() => /^[a-zA-Z0-9\s.,!?'"()\-_&/%:;+*#@\n\r\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+$/u, []);
  const todayStr    = useMemo(() => new Date().toISOString().split("T")[0], []);

  const validateField = useCallback((field, value, formStateOverride) => {
    let err = "";
    const v = typeof value === "string" ? value.trim() : value;
    const currentState = formStateOverride || { title, body, scheduled, schedDate, schedTime };

    if (field === "title") {
      if (!v) {
        err = "Notification title is required.";
      } else if (v.length < 3) {
        err = "Title must be at least 3 characters.";
      } else if (v.length > 65) {
        err = "Title cannot exceed 65 characters.";
      } else if (!/[a-zA-Z0-9\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(v)) {
        err = "Title must contain readable text or words, not just special symbols.";
      } else if (/([!@#$%^&*()_+={}\[\]:;<>,.?\/\\~`|\-])\1{2,}/.test(v) || /[!@#$%^&*()_+={}\[\]:;<>,.?\/\\~`|\-]{4,}/.test(v)) {
        err = "Title cannot contain special character spam (e.g. !@!!@#%&*()%_+).";
      } else if (/[<>]/.test(v) || !TITLE_REGEX.test(v)) {
        err = "Title contains invalid characters (angle brackets & HTML tags not allowed).";
      }
    } else if (field === "body") {
      if (!v) {
        err = "Notification message body is required.";
      } else if (v.length < 5) {
        err = "Message body must be at least 5 characters.";
      } else if (v.length > 220) {
        err = "Message body cannot exceed 220 characters.";
      } else if (!/[a-zA-Z0-9\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(v)) {
        err = "Message must contain readable text or words, not just special symbols.";
      } else if (/([!@#$%^&*()_+={}\[\]:;<>,.?\/\\~`|\-])\1{2,}/.test(v) || /[!@#$%^&*()_+={}\[\]:;<>,.?\/\\~`|\-]{4,}/.test(v)) {
        err = "Message cannot contain special character spam (e.g. !@%*)(****&%).";
      } else if (/[<>]/.test(v) || !BODY_REGEX.test(v)) {
        err = "Message contains invalid characters (angle brackets & HTML tags not allowed).";
      }
    } else if (field === "schedDate" && currentState.scheduled) {
      if (!v) {
        err = "Please select a broadcast date.";
      } else if (v < todayStr) {
        err = "Past dates cannot be selected for scheduled broadcast.";
      } else if (currentState.schedTime) {
        const targetDateTime = new Date(`${v}T${currentState.schedTime}`);
        if (isNaN(targetDateTime.getTime())) {
          err = "Invalid date selection.";
        } else if (targetDateTime <= new Date()) {
          err = "Scheduled date & time must be in the future.";
        }
      }
    } else if (field === "schedTime" && currentState.scheduled) {
      if (!v) {
        err = "Please select a broadcast time.";
      } else if (currentState.schedDate) {
        const targetDateTime = new Date(`${currentState.schedDate}T${v}`);
        if (isNaN(targetDateTime.getTime())) {
          err = "Invalid time selection.";
        } else if (targetDateTime <= new Date()) {
          err = "Scheduled date & time must be in the future.";
        }
      }
    }

    return err;
  }, [title, body, scheduled, schedDate, schedTime, TITLE_REGEX, BODY_REGEX, todayStr]);

  const handleTitleChange = (val) => {
    setTitle(val);
    setTouched(prev => ({ ...prev, title: true }));
    setErrors(prev => ({ ...prev, title: validateField("title", val) }));
  };

  const handleBodyChange = (val) => {
    setBody(val);
    setTouched(prev => ({ ...prev, body: true }));
    setErrors(prev => ({ ...prev, body: validateField("body", val) }));
  };

  const handleSchedDateChange = (val) => {
    setSchedDate(val);
    setTouched(prev => ({ ...prev, schedDate: true }));
    const fState = { title, body, scheduled, schedDate: val, schedTime };
    setErrors(prev => ({
      ...prev,
      schedDate: validateField("schedDate", val, fState),
      schedTime: schedTime ? validateField("schedTime", schedTime, fState) : prev.schedTime
    }));
  };

  const handleSchedTimeChange = (val) => {
    setSchedTime(val);
    setTouched(prev => ({ ...prev, schedTime: true }));
    const fState = { title, body, scheduled, schedDate, schedTime: val };
    setErrors(prev => ({
      ...prev,
      schedTime: validateField("schedTime", val, fState),
      schedDate: schedDate ? validateField("schedDate", schedDate, fState) : prev.schedDate
    }));
  };

  const validateAll = () => {
    const fState = { title, body, scheduled, schedDate, schedTime };
    const newErrors = {
      title: validateField("title", title, fState),
      body: validateField("body", body, fState),
    };

    if (scheduled) {
      newErrors.schedDate = validateField("schedDate", schedDate, fState);
      newErrors.schedTime = validateField("schedTime", schedTime, fState);
    }

    setTouched({ title: true, body: true, schedDate: true, schedTime: true });
    setErrors(newErrors);

    return !Object.values(newErrors).some(e => Boolean(e));
  };

  const fetchUsers = async () => {
    try {
      const res = await adminApi.getUsers({ pageSize: 1000 });
      if (res.success && res.data) {
        setUsers(res.data.users || res.data || []);
      }
    } catch (err) {
      console.warn("Failed to load users for notification reach stats:", err);
    }
  };

  const [packages, setPackages] = useState([]);

  const fetchPackages = async () => {
    try {
      const res = await adminApi.getPackages();
      if (res.success && res.data) {
        setPackages(res.data.packages || res.data || []);
      }
    } catch (err) {
      console.warn("Failed to load packages for dynamic tier options:", err);
    }
  };

  const dynamicTiers = useMemo(() => {
    const normalizeTier = (str) => {
      if (!str) return "";
      const lower = str.toLowerCase().trim();
      if (lower.includes("all")) return "All Users";
      if (lower.includes("ytt") || lower.includes("pro")) return "YTT Pro";
      if (lower.includes("premium")) return "Premium";
      if (lower.includes("standard")) return "Standard";
      if (lower.includes("basic")) return "Basic";
      if (lower.includes("free")) return "Free";
      return str.trim();
    };

    const result = ["All Users", "Free", "Basic", "Standard", "Premium", "YTT Pro"];
    const seenLower = new Set(result.map(t => t.toLowerCase()));

    (packages || []).forEach(p => {
      const raw = (p.packageType || p.name || p.PackageType || p.Name || "").trim();
      if (!raw) return;
      const canonical = normalizeTier(raw);
      if (!seenLower.has(canonical.toLowerCase())) {
        seenLower.add(canonical.toLowerCase());
        result.push(canonical);
      }
    });

    return result;
  }, [packages]);

  const fetchSubscriptions = async () => {
    try {
      const res = await adminApi.getSubscriptions({ pageSize: 1000 });
      if (res.success && res.data) {
        setSubscriptions(res.data.subscriptions || res.data || []);
      }
    } catch (err) {
      console.warn("Failed to load subscriptions:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await notificationAPI.getAdminNotificationHistory();
      if (res.data?.success) {
        setHistory(res.data.data || []);
      } else if (res.success) {
        setHistory(res.data || []);
      }
    } catch (err) {
      console.warn("Failed to load admin notification history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchUsers();
    fetchSubscriptions();
    fetchPackages();
  }, []);

  const mappedHistory = useMemo(() => {
    return (history || []).map(n => {
      const d = new Date(n.createdAt || n.CreatedAt);
      const schedTime = n.scheduledAt || n.ScheduledAt;
      const isScheduled = schedTime && new Date(schedTime) > new Date();
      const rawChannel = (n.channel ?? n.Channel ?? "push").toLowerCase();
      const mappedChannel = rawChannel === "email" ? "email" : rawChannel === "both" ? "both" : "push";

      return {
        id: n.id ?? n.Id,
        title: n.title ?? n.Title,
        message: n.message ?? n.Message,
        audience: n.audience ?? n.Audience ?? "All Users",
        channel: mappedChannel,
        status: isScheduled ? "Scheduled" : "Sent",
        sent: d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }),
        recipients: n.recipients ?? n.Recipients ?? 1,
        delivered: n.delivered ?? n.Delivered ?? 1,
        opened: n.opened ?? n.Opened ?? 0,
        scheduledAt: schedTime,
        createdTs: isNaN(d.getTime()) ? 0 : d.getTime(),
        scheduledFor: schedTime ? new Date(schedTime).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "—",
      };
    });
  }, [history]);

  const showToast = useCallback((msg) => {
    setToast(msg); setTimeout(() => setToast(null), 3200);
  }, []);

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setBody(tpl.body);
    setAudience(tpl.audience);
    setChannel(tpl.channel);
    setSelectedTemplate(tpl.id);
    setErrors({ title: "", body: "", schedDate: "", schedTime: "" });
    setTouched({ title: false, body: false, schedDate: false, schedTime: false });
    showToast(`Template "${tpl.name}" applied`);
  };

  const handleSend = async () => {
    if (!validateAll()) {
      showToast("Please correct the highlighted validation errors before sending", "error");
      return;
    }
    setSending(true);
    try {
      let scheduledAtVal = null;
      if (scheduled && schedDate && schedTime) {
        scheduledAtVal = new Date(`${schedDate}T${schedTime}`).toISOString();
      }

      const res = await notificationAPI.sendNotification({
        title: title.trim(),
        message: body.trim(),
        audience: audience,
        channel: channel,
        scheduledAt: scheduledAtVal,
        updateFromNotificationId: editingId
      });
      if (res.data?.success || res.success) {
        setTitle("");
        setBody("");
        setScheduled(false);
        setSchedDate("");
        setSchedTime("");
        setEditingId(null);
        setErrors({ title: "", body: "", schedDate: "", schedTime: "" });
        setTouched({ title: false, body: false, schedDate: false, schedTime: false });
        showToast(editingId
          ? `✓ Notification updated successfully!`
          : scheduledAtVal 
          ? `✓ Notification scheduled successfully for ${new Date(scheduledAtVal).toLocaleString()}`
          : `✓ Notification sent successfully to ${audience} via ${channel === "both" ? "Push & Email" : channel.toUpperCase()}`
        );
        await fetchHistory();
      }
    } catch (err) {
      showToast(`Error: ${err.message || "Failed to send notification"}`);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = (id, isScheduled = false) => {
    setDeleteConfirmId(id);
    setDeleteIsScheduled(isScheduled);
  };

  const executeDeleteNotification = async () => {
    if (!deleteConfirmId) return;
    try {
      const res = await notificationAPI.deleteNotification(deleteConfirmId);
      if (res.data?.success || res.success) {
        showToast(deleteIsScheduled ? "✓ Scheduled notification cancelled successfully" : "✓ Notification deleted successfully");
        await fetchHistory();
      }
    } catch (err) {
      showToast(`Error: ${err.message || "Failed to delete notification"}`);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const executeDeleteAllNotifications = async () => {
    setDeletingAll(true);
    try {
      const res = await notificationAPI.deleteAllNotifications();
      if (res.data?.success || res.status === 200 || res.success) {
        setHistory([]);
        setDeleteAllConfirmOpen(false);
        showToast("✓ All notifications permanently deleted from database");
        await fetchHistory();
      } else {
        showToast("Failed to delete all notifications", "error");
      }
    } catch (err) {
      console.error("Delete all notifications error:", err);
      showToast(err.response?.data?.message || err.message || "Failed to delete all notifications", "error");
    } finally {
      setDeletingAll(false);
    }
  };

  const handleEditScheduled = (n) => {
    setTitle(n.title);
    setBody(n.message);
    setChannel(n.channel || "push");
    
    // Determine audienceType (By Tier vs By Segment)
    const isTier = dynamicTiers.some(t => t.toLowerCase() === (n.audience || "").toLowerCase()) || TIERS.includes(n.audience);
    setAudienceType(isTier ? "tier" : "segment");
    setAudience(n.audience);

    if (n.scheduledAt) {
      const d = new Date(n.scheduledAt);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const date = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const mins = String(d.getMinutes()).padStart(2, "0");
      setSchedDate(`${year}-${month}-${date}`);
      setSchedTime(`${hours}:${mins}`);
      setScheduled(true);
    }
    setEditingId(n.id);
    showToast("Loaded scheduled notification into Composer for editing");
  };

  const handleResend = async (notif) => {
    setSending(true);
    try {
      const targetAudience = notif.audience === "All Users" ? "All Users" : notif.audience.replace("User ID: ", "");
      const res = await notificationAPI.sendNotification({
        title: notif.title,
        message: notif.message,
        audience: targetAudience,
        channel: notif.channel || "push"
      });
      if (res.data?.success || res.success) {
        showToast(`✓ Notification successfully resent to ${targetAudience}`);
        await fetchHistory();
      }
    } catch (err) {
      showToast(`Error: ${err.message || "Failed to resend"}`);
    } finally {
      setSending(false);
    }
  };

  const handleDuplicate = (notif) => {
    setTitle(notif.title);
    setBody(notif.message);
    const targetAudience = notif.audience === "All Users" ? "All Users" : notif.audience.replace("User ID: ", "");
    setAudience(targetAudience);
    setChannel(notif.channel || "push");
    showToast("Copied details to Composer");
  };

  const handleExportAll = () => {
    if (!filteredHistory.length) {
      showToast("No data to export");
      return;
    }
    const headers = ["Notification Title", "Message", "Audience", "Channel", "Status", "Sent At", "Reach", "Open Rate"];
    const rows = filteredHistory.map(n => [
      n.title,
      n.message,
      n.audience,
      n.channel,
      n.status,
      n.sent,
      n.recipients,
      `${Math.round(n.opened / n.recipients * 100)}%`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.map(val => `"${val.toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `notifications_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ CSV download started");
  };

  const getUserTier = (user) => {
    const userSub = (subscriptions || []).find(s => s.userId === user.id && s.status === "Active");
    return userSub?.package?.packageType || "Free";
  };

  const getTierRank = (tier) => {
    if (!tier) return 0;
    const low = tier.toLowerCase().trim();
    if (low.includes("pro") || low.includes("ytt")) return 4;
    if (low.includes("premium") || low.includes("premiun")) return 3;
    if (low.includes("standard")) return 2;
    if (low.includes("basic")) return 1;
    return 0; // Free
  };

  const isUserEligibleForTier = (userTier, targetTier) => {
    if (!targetTier || targetTier === "All Users" || targetTier === "All") return true;
    return getTierRank(userTier) <= getTierRank(targetTier);
  };

  const handleExportRecipients = async (notif) => {
    showToast("Preparing recipients report…");
    const headers = ["User ID", "User Name", "Email", "User Tier", "Delivery Status", "Opened"];
    let rows = [];

    try {
      const res = await notificationAPI.getNotificationRecipients(notif.id);
      if (res.data?.success && res.data.data) {
        rows = res.data.data.map(r => [
          r.userId,
          r.fullName,
          r.email || "N/A",
          r.userTier,
          r.delivered,
          r.opened
        ]);
      }
    } catch (err) {
      console.warn("Failed to fetch recipients from API, falling back to local list:", err);
      // Fallback to local list calculation if API fails
      const audienceStr = notif.audience === "All Users" ? "All Users" : notif.audience.replace("User ID: ", "");
      const openedStatus = notif.opened > 0 ? "Yes" : "No";
      if (audienceStr === "All Users") {
        rows = users.map(u => [u.id, u.fullName || u.userName, u.email || "N/A", getUserTier(u), "Delivered", openedStatus]);
      } else if (notif.audience.startsWith("User ID: ")) {
        const uid = notif.audience.replace("User ID: ", "");
        const matchedUser = users.find(u => u.id.toString() === uid.toString());
        if (matchedUser) {
          rows = [[matchedUser.id, matchedUser.fullName || matchedUser.userName, matchedUser.email || "N/A", getUserTier(matchedUser), "Delivered", openedStatus]];
        } else {
          rows = [[uid, "N/A", "N/A", "N/A", "Delivered", openedStatus]];
        }
      } else {
        const eligibleUsers = users.filter(u => isUserEligibleForTier(getUserTier(u), audienceStr));
        rows = eligibleUsers.map(u => [u.id, u.fullName || u.userName, u.email || "N/A", getUserTier(u), "Delivered", openedStatus]);
      }
    }

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.map(val => `"${val.toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `recipients_${notif.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ Recipients list exported");
  };

  const filteredHistory = useMemo(() => mappedHistory.filter(n => {
    const q = search.toLowerCase();
    return (
      (!q || n.title.toLowerCase().includes(q) || n.audience.toLowerCase().includes(q)) &&
      (statusF === "all" || n.status === statusF)
    );
  }), [search, statusF, mappedHistory]);

  const dynamicPageSize = pageSize === "all" ? (filteredHistory.length || 1000) : Number(pageSize);

  const paginatedHistory = useMemo(() => {
    if (pageSize === "all") return filteredHistory;
    const startIndex = (currentPage - 1) * dynamicPageSize;
    return filteredHistory.slice(startIndex, startIndex + dynamicPageSize);
  }, [filteredHistory, currentPage, pageSize, dynamicPageSize]);

  const totalPages = pageSize === "all" ? 1 : Math.ceil(filteredHistory.length / dynamicPageSize) || 1;

  const scheduledNotifs = mappedHistory.filter(n => n.status === "Scheduled");
  const drawerNotif = drawerId ? mappedHistory.find(n => n.id === drawerId) : null;

  // Stats
  const totalSent      = mappedHistory.filter(n => n.status === "Sent").length;
  const sentThisWeek   = mappedHistory.filter(n => n.status === "Sent" && n.createdTs >= Date.now() - 7 * 24 * 60 * 60 * 1000).length;
  const totalRecipients = mappedHistory.reduce((a,n) => a + n.recipients, 0);
  const avgOpenRate    = mappedHistory.length ? Math.round(mappedHistory.reduce((a,n) => a + (n.opened/n.recipients*100), 0) / mappedHistory.length) : 0;
  const failedCount    = mappedHistory.filter(n => n.status === "Failed").length;

  const inputSty = {
    width:"100%", border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)",
    borderRadius:14, padding:"10px 14px", fontSize:13, fontFamily:"'Inter',sans-serif",
    color:T.dark, outline:"none", transition:"border-color 0.2s, box-shadow 0.2s",
  };
  const focusStyle = (e) => { e.target.style.borderColor=T.olive; e.target.style.boxShadow=`0 0 0 4px rgba(107,138,90,0.1)`; };
  const blurStyle  = (e) => { e.target.style.borderColor=T.border; e.target.style.boxShadow="none"; };

  if (loadingHistory && (!history || history.length === 0)) {
    return (
      <AdminDashboardLayout activeNav="notifs">
        <BrandLoader 
          message="Loading Notification Broadcasts..." 
          subMessage="Fetching sent alerts, scheduled broadcasts & delivery tracking" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout activeNav="notifs">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-18,0], x:[0,14,0] }}
          transition={{ duration:14, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",
            filter:"blur(80px)", top:-140, right:-80, zIndex:0 }}/>
        <motion.div animate={{ y:[0,14,0], x:[0,-10,0] }}
          transition={{ duration:17, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent 70%)",
            filter:"blur(80px)", bottom:-80, left:40, zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0">

          {/* Topbar */}
          <motion.div initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
            className="ad-toolbar flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>
                Notifications
              </h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                {totalSent} sent · {scheduledNotifs.length} scheduled · {avgOpenRate}% avg open rate
              </p>
              {/* Retention is enforced server-side — history only ever shows the
                  last 21 days because older rows are deleted from the database. */}
              <p className="text-[11px] mt-1 flex items-center gap-1.5" style={{ color:T.muted }}>
                <FiClock size={11}/> History covers the last 21 days · older notifications are deleted automatically
              </p>
            </div>
            <div className="ad-wrap flex gap-2 items-center">
              <motion.button whileHover={{ y:-1 }} onClick={() => setDeleteAllConfirmOpen(true)}
                className="h-9 px-4 rounded-full text-[12px] font-bold flex items-center gap-1.5 border-none text-white bg-[#E24B4A] hover:bg-[#c93f3e] cursor-pointer transition-all shadow-[0_4px_14px_rgba(226,75,74,0.3)]">
                <FiTrash2 size={13}/> Delete All Notifications
              </motion.button>
              <motion.button whileHover={{ y:-1 }} onClick={handleExportAll}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                  color:T.mid, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Export
              </motion.button>
            </div>
          </motion.div>
          {/* KPI strip */}
          <div className="ad-kpi grid grid-cols-5 gap-3 mb-5">
            <StatCard icon={FiSend}        value={totalSent}      label="Total Sent"     trend={`+${sentThisWeek} this week`}
              iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
              accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
              cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.06}/>
            <StatCard icon={FiUsers}       value={totalRecipients.toLocaleString()} label="Total Reach" trend="Cumulative"
              iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
              accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
              cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.1}/>
            <StatCard icon={FiCheckCircle} value={`${avgOpenRate}%`} label="Avg Open Rate" trend="Across all sent"
              iconBg="rgba(34,197,94,0.2)" accentColor={T.green} delay={0.14}/>
            <StatCard icon={FiClock}       value={scheduledNotifs.length} label="Scheduled"   trend="Upcoming"
              iconBg="rgba(24,95,165,0.15)" accentColor={T.blue} delay={0.18}/>
            <StatCard icon={FiBell}        value={failedCount}    label="Failed"        trend="Needs retry" trendUp={false}
              iconBg="rgba(226,75,74,0.15)" accentColor={T.red}
              cardBg="linear-gradient(145deg,rgba(226,75,74,0.06),rgba(252,235,235,0.3))" delay={0.22}/>
          </div>

          {/* Main grid   Compose + History */}
          <div className="ad-1col grid gap-5" style={{ gridTemplateColumns:"380px 1fr" }}>

            {/* ── LEFT: COMPOSE PANEL ── */}
            <motion.div initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
              transition={{ delay:0.1, duration:0.6, ease:[0.22,1,0.36,1] }}
              className="flex flex-col gap-4 relative z-30">

              {/* Compose card */}
              <div className="rounded-[24px] p-5 relative z-40" style={{ ...glass, position: "relative", zIndex: 40 }}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-[14px] flex items-center justify-center"
                      style={{ background: editingId ? `linear-gradient(135deg,${T.orange},${T.orange2})` : `linear-gradient(135deg,${T.olive},${T.olive2})`,
                        boxShadow: editingId ? `0 5px 14px rgba(242,154,12,0.3)` : `0 5px 14px rgba(107,138,90,0.4)` }}>
                      <FiBell size={18} style={{ color:"#fff" }}/>
                    </div>
                    <div>
                      <p className="font-black text-[15px] text-[#1F1F1F]"
                        style={{ fontFamily:"'Poppins',sans-serif" }}>{editingId ? "Edit Notification" : "Compose Notification"}</p>
                      <p className="text-[11px]" style={{ color:T.muted }}>{editingId ? "Modify scheduled notification" : "Send to users instantly or schedule"}</p>
                    </div>
                  </div>
                  {editingId && (
                    <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setEditingId(null);
                        setTitle("");
                        setBody("");
                        setScheduled(false);
                        setSchedDate("");
                        setSchedTime("");
                        showToast("Editing cancelled");
                      }}
                      className="px-2.5 py-1.5 rounded-[8px] border-none text-[10px] font-black uppercase tracking-[0.5px] cursor-pointer text-white bg-[#E24B4A] hover:bg-[#c93f3e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      Cancel
                    </motion.button>
                  )}
                </div>

                <div className="flex flex-col gap-3.5">
                  {/* Title */}
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5"
                      style={{ color: errors.title && touched.title ? T.red : T.muted }}>Notification Title *</label>
                    <input value={title} onChange={e => handleTitleChange(e.target.value)}
                      style={{
                        ...inputSty,
                        borderColor: errors.title && touched.title ? T.red : T.border
                      }}
                      placeholder="e.g. Your session starts in 30 min"
                      onFocus={focusStyle} onBlur={e => { blurStyle(e); setTouched(prev => ({ ...prev, title: true })); }}/>
                    <div className="flex items-center justify-between mt-1">
                      {errors.title && touched.title ? (
                        <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1">
                          <FiX size={12}/> {errors.title}
                        </p>
                      ) : <span />}
                      <p className="text-[10px] text-right ml-auto" style={{ color: title.length > 60 ? T.red : T.muted }}>
                        {title.length}/65
                      </p>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5"
                      style={{ color: errors.body && touched.body ? T.red : T.muted }}>Message *</label>
                    <textarea value={body} onChange={e => handleBodyChange(e.target.value)}
                      rows={4}
                      style={{
                        ...inputSty,
                        borderColor: errors.body && touched.body ? T.red : T.border,
                        resize: "none",
                        lineHeight: 1.6
                      }}
                      placeholder="Write your notification message here…"
                      onFocus={focusStyle} onBlur={e => { blurStyle(e); setTouched(prev => ({ ...prev, body: true })); }}/>
                    <div className="flex items-center justify-between mt-1">
                      {errors.body && touched.body ? (
                        <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1">
                          <FiX size={12}/> {errors.body}
                        </p>
                      ) : <span />}
                      <p className="text-[10px] text-right ml-auto" style={{ color: body.length > 200 ? T.red : T.muted }}>
                        {body.length}/220
                      </p>
                    </div>
                  </div>

                  {/* Audience type toggle */}
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-2"
                      style={{ color:T.muted }}>Audience</label>
                    <div className="flex gap-1.5 mb-2.5">
                      {[["segment","By Segment"],["tier","By Tier"]].map(([v,l]) => (
                        <motion.button key={v} whileTap={{ scale:0.95 }}
                          onClick={() => {
                            setAudienceType(v);
                            setAudience(v === "tier" ? dynamicTiers[0] : SEGMENTS[0]);
                          }}
                          className="flex-1 py-2 rounded-[12px] text-[12px] font-semibold border-none cursor-pointer"
                          style={audienceType===v
                            ? { background:`linear-gradient(135deg,${T.olive},${T.olive2})`, color:"#fff",
                                boxShadow:`0 4px 12px rgba(107,138,90,0.3)`, fontFamily:"'Inter',sans-serif" }
                            : { background:"rgba(239,229,216,0.4)", color:T.mid, fontFamily:"'Inter',sans-serif" }}>
                          {l}
                        </motion.button>
                      ))}
                    </div>
                    <div className="relative">
                      <button type="button" onClick={() => setAudienceOpen(!audienceOpen)}
                        className="w-full h-12 rounded-[14px] px-4 flex items-center justify-between border cursor-pointer text-[13px] outline-none"
                        style={{
                          background: "rgba(255,255,255,0.82)",
                          borderColor: audienceOpen ? T.olive : T.border,
                          color: T.dark,
                          fontFamily: "'Inter',sans-serif",
                          textAlign: "left"
                        }}>
                        <span>{audience}</span>
                        <FiChevronDown size={14} className={`transition-transform duration-200 ${audienceOpen ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {audienceOpen && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setAudienceOpen(false)} />
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                              className="absolute left-0 right-0 mt-1.5 rounded-[16px] border shadow-lg overflow-hidden z-50 p-1"
                              style={{ background: "rgba(255,255,255,0.98)", borderColor: T.border }}>
                              {(audienceType === "tier" ? dynamicTiers : SEGMENTS).map(o => (
                                <div key={o} onClick={() => { setAudience(o); setAudienceOpen(false); }}
                                  className="px-4 py-2.5 rounded-[10px] text-[13px] hover:bg-gray-50 cursor-pointer font-medium transition-colors"
                                  style={{
                                    background: audience === o ? "rgba(107,138,90,0.08)" : "transparent",
                                    color: audience === o ? T.olive : "#374151"
                                  }}>
                                  {o}
                                </div>
                              ))}
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Channel */}
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-2"
                      style={{ color:T.muted }}>Channel</label>
                    <div className="flex flex-col gap-2">
                      {CHANNELS.map(ch => (
                        <motion.button key={ch.id} whileTap={{ scale:0.98 }}
                          onClick={() => setChannel(ch.id)}
                          className="flex items-center gap-3 p-3 rounded-[14px] border cursor-pointer text-left"
                          style={{
                            background: channel===ch.id ? `${T.olive}0A` : "rgba(255,255,255,0.7)",
                            borderColor: channel===ch.id ? `${T.olive}35` : T.border,
                            boxShadow: channel===ch.id ? `0 4px 14px rgba(107,138,90,0.1)` : "none",
                            transition:"all 0.22s",
                          }}>
                          <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0"
                            style={{ background: channel===ch.id ? `linear-gradient(135deg,${T.olive},${T.olive2})` : "rgba(239,229,216,0.5)" }}>
                            <ch.icon size={14} style={{ color: channel===ch.id ? "#fff" : T.muted }}/>
                          </div>
                          <div className="flex-1">
                            <p className="text-[12px] font-bold" style={{ color:channel===ch.id?T.olive:T.dark }}>{ch.label}</p>
                            <p className="text-[10px]" style={{ color:T.muted }}>{ch.desc}</p>
                          </div>
                          {channel===ch.id && (
                            <div className="w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>
                              <FiCheckCircle size={10} style={{ color:"#fff" }}/>
                            </div>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Schedule toggle */}
                  <div className="flex items-center justify-between p-3.5 rounded-[14px]"
                    style={{ background:"rgba(107,138,90,0.07)", border:"1px solid rgba(107,138,90,0.18)" }}>
                    <div className="flex items-center gap-2.5">
                      <FiCalendar size={15} style={{ color:T.olive }}/>
                      <div>
                        <p className="text-[12px] font-bold text-[#1F1F1F]">Schedule for later</p>
                        <p className="text-[10px]" style={{ color:T.muted }}>Set a specific date & time</p>
                      </div>
                    </div>
                    <motion.button type="button" onClick={() => {
                      const nextSched = !scheduled;
                      setScheduled(nextSched);
                      if (!nextSched) {
                        setErrors(prev => ({ ...prev, schedDate: "", schedTime: "" }));
                      }
                    }}
                      className="relative rounded-full border-none cursor-pointer flex-shrink-0"
                      style={{
                        width:38, height:22,
                        background: scheduled ? `linear-gradient(135deg,${T.olive},${T.olive2})` : "rgba(239,229,216,0.9)",
                        boxShadow: scheduled ? `0 3px 10px rgba(107,138,90,0.4)` : "none",
                        transition:"all 0.25s",
                      }}>
                      <motion.div animate={{ left: scheduled ? 18 : 2 }}
                        transition={{ type:"spring", stiffness:400, damping:28 }}
                        className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white"
                        style={{ boxShadow:"0 2px 4px rgba(0,0,0,0.2)" }}/>
                    </motion.button>
                  </div>

                  {/* Date/time picker */}
                  <AnimatePresence>
                    {scheduled && (
                      <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                        exit={{ height:0, opacity:0 }} transition={{ duration:0.3 }}
                        className="overflow-visible relative z-30">
                        <CustomSchedulePicker
                          schedDate={schedDate}
                          schedTime={schedTime}
                          onDateChange={handleSchedDateChange}
                          onTimeChange={handleSchedTimeChange}
                          errors={errors}
                          touched={touched}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Preview strip */}
                  {(title || body) && (
                    <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                      className="rounded-[16px] p-3.5"
                      style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)" }}>
                      <p className="text-[9px] font-black uppercase tracking-[1px] mb-2"
                        style={{ color:"rgba(255,255,255,0.4)" }}>Preview</p>
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-[9px] flex items-center justify-center flex-shrink-0"
                          style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                          <span className="text-white font-black text-[11px]"
                            style={{ fontFamily:"'Poppins',sans-serif" }}>DY</span>
                        </div>
                        <div>
                          <p className="text-[12px] font-bold text-white leading-tight">
                            {title || "Notification title"}
                          </p>
                          <p className="text-[11px] mt-0.5 leading-relaxed"
                            style={{ color:"rgba(255,255,255,0.55)" }}>
                            {body.slice(0,80) || "Message preview…"}{body.length>80?"…":""}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Send button */}
                  <motion.button whileHover={!sending ? { y:-2 } : {}}
                    whileTap={!sending ? { scale:0.97 } : {}}
                    onClick={handleSend} disabled={sending}
                    className="w-full h-12 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2.5 border-none cursor-pointer relative overflow-hidden"
                    style={{
                      background: sending
                        ? `linear-gradient(135deg,${T.olive},${T.olive2})`
                        : editingId
                        ? `linear-gradient(135deg,${T.orange},${T.orange2})`
                        : scheduled
                        ? `linear-gradient(135deg,${T.blue},#2D7DD2)`
                        : `linear-gradient(135deg,${T.olive},${T.olive2})`,
                      boxShadow: sending ? "none" : editingId ? `0 8px 24px rgba(242,154,12,0.3)` : `0 8px 24px rgba(107,138,90,0.4)`,
                      fontFamily:"'Inter',sans-serif",
                      transition:"box-shadow 0.3s",
                    }}>
                    {/* Shine */}
                    <div className="absolute inset-0 -translate-x-full hover:translate-x-full transition-all duration-[900ms]"
                      style={{ background:"linear-gradient(120deg,transparent,rgba(255,255,255,0.2),transparent)", transform:"skewX(14deg)" }}/>
                    {sending ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Saving…
                      </>
                    ) : (
                      <>
                        {editingId ? <FiCheckCircle size={16}/> : scheduled ? <FiClock size={16}/> : <FiSend size={16}/>}
                        {editingId ? "Update Notification" : scheduled ? "Schedule Notification" : "Send Now"}
                      </>
                    )}
                  </motion.button>
                </div>
              </div>

              {/* Templates */}
              <div className="rounded-[24px] p-5 relative z-10" style={{ ...glass, position: "relative", zIndex: 10 }}>
                <p className="font-black text-[14px] text-[#1F1F1F] mb-4"
                  style={{ fontFamily:"'Poppins',sans-serif" }}>Quick Templates</p>
                <div className="flex flex-col gap-2">
                  {TEMPLATES.map(tpl => (
                    <motion.div key={tpl.id}
                      whileHover={{ x:4, background:"rgba(255,255,255,0.95)", borderColor:`rgba(107,138,90,0.3)` }}
                      onClick={() => applyTemplate(tpl)}
                      className="flex items-center gap-3 p-3 rounded-[14px] border cursor-pointer transition-all"
                      style={{
                        background: selectedTemplate===tpl.id ? "rgba(107,138,90,0.06)" : "rgba(255,255,255,0.7)",
                        borderColor: selectedTemplate===tpl.id ? `${T.olive}35` : T.border,
                      }}>
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0"
                        style={{ 
                          background: selectedTemplate===tpl.id ? "rgba(107,138,90,0.15)" : "rgba(239,229,216,0.3)",
                          color: selectedTemplate===tpl.id ? T.olive : T.mid 
                        }}>
                        <tpl.icon size={15}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-[#1F1F1F] truncate">{tpl.name}</p>
                        <p className="text-[10px] truncate" style={{ color:T.muted }}>{tpl.audience}</p>
                      </div>
                      <ChannelBadge channel={tpl.channel}/>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* ── RIGHT: HISTORY + SCHEDULED ── */}
            <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.14, duration:0.6 }}
              className="flex flex-col gap-4">

              {/* Tab bar */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-1 p-1.5 rounded-[18px]"
                  style={{ background:"rgba(255,255,255,0.82)", border:"1px solid rgba(255,255,255,0.95)",
                    backdropFilter:"blur(20px)" }}>
                  {[["history","History"],["scheduled","Scheduled"],].map(([v,l]) => (
                    <motion.button key={v} whileTap={{ scale:0.95 }} onClick={() => setTab(v)}
                      className="px-5 py-2 rounded-[13px] text-[12px] font-semibold border-none cursor-pointer"
                      style={tab===v
                        ? { background:`linear-gradient(135deg,${T.olive},${T.olive2})`, color:"#fff",
                            boxShadow:`0 5px 16px rgba(107,138,90,0.35)`, fontFamily:"'Inter',sans-serif" }
                        : { background:"transparent", color:"#888", fontFamily:"'Inter',sans-serif" }}>
                      {l}
                      {v==="scheduled" && scheduledNotifs.length > 0 && (
                        <span className="ml-1.5 text-[10px] font-black px-1.5 py-0.5 rounded-full"
                          style={{ background:"rgba(24,95,165,0.15)", color:T.blue }}>
                          {scheduledNotifs.length}
                        </span>
                      )}
                    </motion.button>
                  ))}
                </div>
                {/* Filters */}
                <div className="ad-filters ad-full flex gap-2 items-center">
                  <div className="ad-filters-full flex items-center gap-2 rounded-[14px] px-3.5 py-2.5"
                    style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}` }}>
                    <FiBell size={13} style={{ color:T.light }}/>
                    <input value={search} onChange={e => setSearch(e.target.value)}
                      placeholder="Search notifications…"
                      className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F]"
                      style={{ fontFamily:"'Inter',sans-serif", width:180 }}/>
                  </div>
                  <div className="relative">
                    <button type="button" onClick={() => setStatusFOpen(!statusFOpen)}
                      className="rounded-full px-4 py-2.5 text-[12px] font-semibold border cursor-pointer flex items-center gap-1.5 outline-none"
                      style={{
                        background: "rgba(255,255,255,0.82)",
                        borderColor: T.border,
                        color: T.mid,
                        fontFamily: "'Inter',sans-serif",
                      }}>
                      <span>{statusF === "all" ? "All Status" : statusF}</span>
                      <FiChevronDown size={12} className={`transition-transform duration-200 ${statusFOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {statusFOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setStatusFOpen(false)} />
                          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                            className="absolute right-0 mt-1.5 w-40 rounded-[16px] border shadow-lg overflow-hidden z-50 p-1"
                            style={{ background: "rgba(255,255,255,0.98)", borderColor: T.border }}>
                            {["all", "Sent", "Scheduled", "Failed", "Draft"].map(s => (
                              <div key={s} onClick={() => { setStatusF(s); setStatusFOpen(false); }}
                                className="px-3.5 py-2 rounded-[10px] text-[12px] hover:bg-gray-50 cursor-pointer font-medium transition-colors"
                                style={{
                                  background: statusF === s ? "rgba(107,138,90,0.08)" : "transparent",
                                  color: statusF === s ? T.olive : "#4B5563"
                                }}>
                                {s === "all" ? "All Status" : s}
                              </div>
                            ))}
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Page Size Dropdown */}
                  <div className="relative">
                    <button type="button" onClick={() => { setPageSizeOpen(!pageSizeOpen); setStatusFOpen(false); }}
                      className="rounded-full px-3.5 py-2.5 text-[12px] font-bold border cursor-pointer flex items-center gap-1.5 outline-none shadow-2xs"
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        borderColor: T.border,
                        color: T.dark,
                        fontFamily: "'Inter',sans-serif",
                      }}>
                      <span>Rows: {pageSize === "all" ? "All" : pageSize}</span>
                      <FiChevronDown size={12} className={`transition-transform duration-200 ${pageSizeOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {pageSizeOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setPageSizeOpen(false)} />
                          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                            className="absolute right-0 mt-1.5 w-32 rounded-[16px] border shadow-lg overflow-hidden z-50 p-1"
                            style={{ background: "rgba(255,255,255,0.98)", borderColor: T.border }}>
                            {[10, 15, 25, 50, "all"].map(sz => (
                              <div key={sz} onClick={() => { setPageSize(sz); setPageSizeOpen(false); setCurrentPage(1); }}
                                className="px-3.5 py-2 rounded-[10px] text-[12px] hover:bg-amber-50 cursor-pointer font-bold transition-colors flex items-center justify-between"
                                style={{
                                  background: pageSize === sz ? "rgba(107,138,90,0.08)" : "transparent",
                                  color: pageSize === sz ? T.olive : "#4B5563"
                                }}>
                                <span>{sz === "all" ? "All Rows" : `${sz} Rows`}</span>
                                {pageSize === sz && <FiCheck size={12} className="text-emerald-600" />}
                              </div>
                            ))}
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* History table */}
              <AnimatePresence mode="wait">
                {tab === "history" && (
                  <motion.div key="history" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                    className="ad-table rounded-[24px] overflow-hidden" style={glass}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          {["Notification","Audience","Channel","Status","Sent At","Reach","Open Rate","Actions"].map(h => (
                            <th key={h} style={{ fontSize:10, fontWeight:800, textTransform:"uppercase",
                              letterSpacing:"0.9px", color:T.light, padding:"10px 14px",
                              textAlign:h==="Actions"?"right":"left",
                              borderBottom:`1px solid rgba(239,229,216,0.6)`, whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedHistory.filter(n => n.status !== "Scheduled").map((n, i) => {
                          const openPct = Math.round(n.opened / n.recipients * 100);
                          return (
                            <motion.tr key={n.id}
                              initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }}
                              transition={{ delay:i*0.04 }}
                              onClick={() => setDrawerID(n.id)}
                              className="cursor-pointer"
                              onMouseEnter={e => e.currentTarget.style.background="rgba(107,138,90,0.04)"}
                              onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`,
                                maxWidth:200 }}>
                                <p className="text-[13px] font-bold text-[#1F1F1F] truncate">{n.title}</p>
                              </td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`,
                                fontSize:12, color:T.mid, whiteSpace:"nowrap" }}>{n.audience}</td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <ChannelBadge channel={n.channel}/>
                              </td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <StatusPill status={n.status}/>
                              </td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`,
                                fontSize:11, color:T.muted, whiteSpace:"nowrap" }}>{n.sent}</td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`,
                                fontSize:13, fontWeight:700, color:T.dark }}>
                                {n.recipients.toLocaleString()}
                              </td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <div className="flex items-center gap-2">
                                  <div className="h-[4px] rounded-full overflow-hidden"
                                    style={{ width:50, background:"rgba(239,229,216,0.6)" }}>
                                    <div className="h-full rounded-full"
                                      style={{ width:`${openPct}%`,
                                        background:openPct>=40?`linear-gradient(90deg,${T.olive},${T.olive2})`
                                          :`linear-gradient(90deg,${T.orange},${T.orange2})` }}/>
                                  </div>
                                  <span className="text-[12px] font-bold"
                                    style={{ color:openPct>=40?T.olive:T.orange }}>{openPct}%</span>
                                </div>
                              </td>
                              <td style={{ padding:"11px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <div className="flex gap-1.5 justify-end">
                                  {[
                                    { icon:FiEye,      fn:(e)=>{e.stopPropagation();setDrawerID(n.id)}, tip:"View" },
                                    { icon:FiRefreshCw,fn:(e)=>{e.stopPropagation();handleResend(n)}, tip:"Resend" },
                                    { icon:FiTrash2,   fn:(e)=>{e.stopPropagation();handleDeleteNotification(n.id, false)}, tip:"Delete", color:T.red, border:"rgba(226,75,74,0.2)" },
                                  ].map(({ icon:Icon, fn, tip, color, border }) => (
                                    <motion.button key={tip} whileHover={{ y:-1 }} onClick={fn}
                                      className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                                      style={{ background:"rgba(255,255,255,0.9)", borderColor: border || T.border,
                                        color: color || "#888", transition:"all 0.2s" }}>
                                      <Icon size={13}/>
                                    </motion.button>
                                  ))}
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredHistory.filter(n => n.status !== "Scheduled").length === 0 && (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3"
                          style={{ background:"rgba(107,138,90,0.1)" }}>
                          <FiBell size={20} style={{ color:T.olive }}/>
                        </div>
                        <p className="text-[14px] font-semibold text-[#1F1F1F]">No notifications found</p>
                        <p className="text-[12px] mt-1" style={{ color:T.muted }}>Adjust your filters</p>
                      </div>
                    )}
                    {/* Pagination — Smart Scalable Suite (UserManagement UI Style) */}
                    <div
                      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
                      style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
                    >
                      <div className="text-[12px] font-bold text-zinc-500">
                        Showing <span className="text-zinc-900">{filteredHistory.length > 0 ? (currentPage - 1) * dynamicPageSize + 1 : 0}</span> to{" "}
                        <span className="text-zinc-900">{pageSize === "all" ? filteredHistory.length : Math.min(currentPage * dynamicPageSize, filteredHistory.length)}</span> of{" "}
                        <span className="text-zinc-900">{filteredHistory.length}</span> notifications
                      </div>

                      {totalPages > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {/* Active Page Counter Pill */}
                          <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                            Page {currentPage} of {totalPages}
                          </span>

                          {/* Jump to First Page Button */}
                          <motion.button
                            whileTap={{ scale: 0.92 }}
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(1)}
                            title="First Page"
                            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                          >
                            <FiChevronsLeft size={14} />
                          </motion.button>

                          {/* Previous Page Button */}
                          <motion.button
                            whileTap={{ scale: 0.92 }}
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            title="Previous Page"
                            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                          >
                            <FiChevronLeft size={14} /> Prev
                          </motion.button>

                          {/* Smart Windowed Page Number Buttons */}
                          <div className="flex items-center gap-1">
                            {(() => {
                              let pages = [];
                              if (totalPages <= 7) {
                                pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                              } else if (currentPage <= 4) {
                                pages = [1, 2, 3, 4, 5, "...", totalPages];
                              } else if (currentPage >= totalPages - 3) {
                                pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                              } else {
                                pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
                              }

                              return pages.map((item, idx) => {
                                if (item === "...") {
                                  return (
                                    <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                                      •••
                                    </span>
                                  );
                                }

                                const isActive = currentPage === item;
                                return (
                                  <motion.button
                                    key={item}
                                    whileTap={{ scale: 0.92 }}
                                    onClick={() => setCurrentPage(item)}
                                    className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                                      isActive
                                        ? "text-white shadow-xs"
                                        : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                                    }`}
                                    style={
                                      isActive
                                        ? {
                                            background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                                            borderColor: "transparent",
                                            boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                                          }
                                        : {}
                                    }
                                  >
                                    {item}
                                  </motion.button>
                                );
                              });
                            })()}
                          </div>

                          {/* Next Page Button */}
                          <motion.button
                            whileTap={{ scale: 0.92 }}
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            title="Next Page"
                            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                          >
                            Next <FiChevronRight size={14} />
                          </motion.button>

                          {/* Jump to Last Page Button */}
                          <motion.button
                            whileTap={{ scale: 0.92 }}
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            title="Last Page"
                            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                          >
                            <FiChevronsRight size={14} />
                          </motion.button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Scheduled tab */}
                {tab === "scheduled" && (
                  <motion.div key="scheduled" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                    className="flex flex-col gap-3">
                    {scheduledNotifs.length === 0 ? (
                      <div className="rounded-[24px] p-12 text-center" style={glass}>
                        <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mx-auto mb-4"
                          style={{ background:"rgba(24,95,165,0.1)" }}>
                          <FiClock size={22} style={{ color:T.blue }}/>
                        </div>
                        <p className="font-black text-[15px] text-[#1F1F1F]">No scheduled notifications</p>
                        <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                          Use the compose panel to schedule a notification
                        </p>
                      </div>
                    ) : scheduledNotifs.map((n, i) => (
                      <motion.div key={n.id}
                        initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                        transition={{ delay:i*0.06 }}
                        whileHover={{ y:-3, boxShadow:"0 12px 32px rgba(0,0,0,0.08)",
                          borderColor:"rgba(107,138,90,0.3)" }}
                        className="rounded-[20px] p-5 border cursor-pointer"
                        style={{ background:"rgba(255,255,255,0.82)", borderColor:T.border,
                          boxShadow:"0 6px 20px rgba(0,0,0,0.05)", transition:"all 0.25s" }}
                        onClick={() => setDrawerID(n.id)}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1">
                            <p className="font-bold text-[14px] text-[#1F1F1F]">{n.title}</p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className="text-[11px]" style={{ color:T.muted }}>{n.audience}</span>
                              <ChannelBadge channel={n.channel}/>
                            </div>
                          </div>
                          <StatusPill status={n.status}/>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-[12px]" style={{ color:T.blue }}>
                            <FiClock size={13}/>
                            <span className="font-semibold">{n.scheduledFor}</span>
                          </div>
                          <div className="flex gap-1.5">
                            <motion.button whileTap={{ scale:0.9 }}
                              onClick={e => { e.stopPropagation(); handleEditScheduled(n); }}
                              className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                              style={{ background:"rgba(255,255,255,0.9)", borderColor:T.border, color:"#888" }}>
                              <FiEdit3 size={12}/>
                            </motion.button>
                            <motion.button whileTap={{ scale:0.9 }}
                              onClick={e => { e.stopPropagation(); handleDeleteNotification(n.id, true); }}
                              className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                              style={{ background:"rgba(255,255,255,0.9)", borderColor:"rgba(226,75,74,0.2)", color:T.red }}>
                              <FiTrash2 size={12}/>
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>

        {/* Notification detail drawer */}
        <AnimatePresence>
          {drawerNotif && (
            <NotifDrawer notif={drawerNotif} onClose={() => setDrawerID(null)} showToast={showToast} onResend={handleResend} onDuplicate={handleDuplicate} onExport={handleExportRecipients}/>
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div key={toast}
              initial={{ opacity:0, y:16, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }}
              exit={{ opacity:0, y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
                boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
              <FiCheckCircle size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Custom Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirmId && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                onClick={() => setDeleteConfirmId(null)}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity:0, scale:0.95, y:20 }}
                animate={{ opacity:1, scale:1, y:0 }}
                exit={{ opacity:0, scale:0.95, y:20 }}
                transition={{ type:"spring", stiffness:300, damping:25 }}
                className="relative max-w-md w-full rounded-[28px] border border-red-500/20 bg-[#FFFDFB] p-6 shadow-[0_30px_70px_rgba(0,0,0,0.18)] z-10 text-center"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {/* Trash Icon header */}
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4 border border-red-100">
                  <FiTrash2 size={24} className="text-[#E24B4A]" />
                </div>
                <h3 className="text-[18px] font-black text-[#1F1F1F] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {deleteIsScheduled ? "Cancel Scheduled Broadcast?" : "Delete Broadcast?"}
                </h3>
                <p className="text-zinc-500 text-[13px] leading-relaxed mb-6">
                  {deleteIsScheduled 
                    ? "This will cancel the scheduled notification batch. It will not be sent to target users."
                    : "Are you sure you want to delete this notification from history? This will delete the broadcast record for all recipients."}
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setDeleteConfirmId(null)}
                    className="flex-1 h-12 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 text-[13px] font-bold text-zinc-600 cursor-pointer transition-colors"
                  >
                    Keep Notification
                  </button>
                  <button 
                    onClick={executeDeleteNotification}
                    className="flex-1 h-12 rounded-full border-none text-[13px] font-bold text-white cursor-pointer bg-[#E24B4A] hover:bg-[#c93f3e] shadow-[0_8px_20px_rgba(226,75,74,0.3)] transition-all"
                  >
                    Confirm & Delete
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Delete All Confirm Modal */}
        <AnimatePresence>
          {deleteAllConfirmOpen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setDeleteAllConfirmOpen(false)}
                className="fixed inset-0 bg-black/50 backdrop-blur-xs" />
              <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="relative z-[201] w-full max-w-md bg-white border border-red-200 rounded-[28px] p-6 shadow-2xl text-center">
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4 border border-red-100">
                  <FiTrash2 size={24} className="text-[#E24B4A]" />
                </div>
                <h3 className="text-[18px] font-black text-[#1F1F1F] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Delete ALL Notifications?
                </h3>
                <p className="text-zinc-500 text-[13px] leading-relaxed mb-6">
                  ⚠️ WARNING: Are you sure you want to delete ALL notifications? This will permanently wipe all notification history and scheduled batches. This action is irreversible.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setDeleteAllConfirmOpen(false)}
                    disabled={deletingAll}
                    className="flex-1 h-12 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 text-[13px] font-bold text-zinc-600 cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={executeDeleteAllNotifications}
                    disabled={deletingAll}
                    className="flex-1 h-12 rounded-full border-none text-[13px] font-bold text-white cursor-pointer bg-[#E24B4A] hover:bg-[#c93f3e] shadow-[0_8px_20px_rgba(226,75,74,0.3)] transition-all flex items-center justify-center gap-2"
                  >
                    {deletingAll ? "Deleting..." : "Confirm & Delete All"}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}